import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ghp-admin-token", data.token);
      navigate("/admin");
    } catch {
      setError(t("admin.loginError"));
    }
    setLoading(false);
  };

  const inputCls =
    "w-full border border-graphite/20 bg-transparent px-4 py-3.5 text-sm text-graphite focus:outline-none focus:border-forest transition-colors duration-200";

  return (
    <div className="flex min-h-screen items-center justify-center bg-bone structural-grid px-6 pt-16" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-graphite/10 bg-white p-8 md:p-12">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="Green Horizon Project" className="h-12 w-12 object-contain" />
          <div>
            <p className="overline-mono text-forest">Green Horizon Project</p>
            <h1 className="mt-1 font-display text-2xl font-light tracking-tight text-graphite" data-testid="admin-login-title">
              {t("admin.loginTitle")}
            </h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-graphite-soft">{t("admin.loginSub")}</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="overline-mono mb-2 block text-graphite-soft">
              {t("admin.email")}
            </label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="overline-mono mb-2 block text-graphite-soft">
              {t("admin.password")}
            </label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <p className="border-l-2 border-destructive pl-4 text-sm text-destructive" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-3 bg-forest px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-graphite disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? t("admin.loggingIn") : t("admin.login")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </form>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft transition-colors hover:text-forest"
          data-testid="admin-back-to-site"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("admin.backToSite")}
        </Link>
      </div>
    </div>
  );
};

export default AdminLoginPage;
